import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ISensorHardwareDevice, defaultValue } from 'app/shared/model/dto/sensor-hardware-device-dto.model';

const initialState: EntityState<ISensorHardwareDevice> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/odometer';

// Actions

export const getEntities = createAsyncThunk('sensorHardware/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ISensorHardwareDevice[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'odometer/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ISensorHardwareDevice>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getOdometer = createAsyncThunk(
  'odometer/fetch_odometer',
  async (id: string | number) => {
    const requestUrl = `/api/odometer/${id}`;
    return axios.get<ISensorHardwareDevice>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntityForInstallation = createAsyncThunk(
  'odometer/create_entity',
  async (entity: ISensorHardwareDevice, thunkAPI) => {
    const result = await axios.post<ISensorHardwareDevice>(apiUrl + '-for-installation' + `/${entity.deviceTruckImplement.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntityForInstallation = createAsyncThunk(
  'odometer/update_entity',
  async (entity: ISensorHardwareDevice, thunkAPI) => {
    const result = await axios.put<ISensorHardwareDevice>(apiUrl + '-for-installation' + `/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntityOdometer = createAsyncThunk(
  'odometer/delete_entity',
  async (deviceId: string | number, thunkAPI) => {
    const requestUrl = apiUrl + `-for-delete/${deviceId}`;
    const result = await axios.delete<ISensorHardwareDevice>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const SensorHardwareSlice = createEntitySlice({
  name: 'sensorHardware',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getOdometer.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntityOdometer.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntityForInstallation, updateEntityForInstallation), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getOdometer), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(deleteEntityOdometer, createEntityForInstallation, updateEntityForInstallation), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = SensorHardwareSlice.actions;

// Reducer
export default SensorHardwareSlice.reducer;
